import React, { useEffect, useState, useCallback } from "react";
import Header from "../components/Header";
import BottomNav from "../components/BottomNav";
import Avatar from "../components/Avatar";
import RequestInfluencerDialog from "../components/RequestInfluencerDialog";
import { listInfluencerRequests, voteInfluencerRequest } from "../lib/api";
import { ThumbsUp, Plus, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function Requests() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);

  const load = useCallback(() => listInfluencerRequests("pending").then(setRows), []);
  useEffect(() => { load(); }, [load]);

  const upvote = async (rid) => {
    try {
      const r = await voteInfluencerRequest(rid);
      if (r.already) toast("You've already voted.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not vote");
    }
  };

  return (
    <div className="min-h-screen pb-safe" data-testid="requests-page">
      <Header title="Requests" />
      <div className="max-w-xl mx-auto px-4 pt-4 space-y-4">
        <div className="glass-strong rounded-3xl p-5 relative overflow-hidden">
          <div aria-hidden className="pointer-events-none absolute -top-8 -right-8 w-40 h-40 rounded-full opacity-30" style={{ background: "radial-gradient(circle,#B026FF,transparent 70%)" }} />
          <p className="text-[10px] uppercase tracking-widest font-bold text-white/50">Community Requests</p>
          <h1 className="font-display text-3xl font-bold mt-1">Who should we add next?</h1>
          <p className="text-sm text-white/70 mt-2">
            Fans decide the roster. Drop a creator / actor / reality star / athlete you want on Fanpuff. Highest-voted get added first.
          </p>
          <button
            onClick={() => setOpen(true)}
            data-testid="open-request-dialog"
            className="mt-4 inline-flex items-center gap-2 px-4 py-2.5 rounded-full font-bold text-black"
            style={{ background: "linear-gradient(90deg,#00F0FF,#B026FF)" }}
          >
            <Plus className="w-4 h-4" /> Add a request
          </button>
        </div>

        <section className="space-y-2">
          <h2 className="font-display text-xl font-bold flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#FFD600]" /> Top voted
          </h2>
          {rows.length === 0 && (
            <div className="glass rounded-2xl p-5 text-sm text-white/60 text-center">
              No requests yet — you could be the first to spark the list 🔥
            </div>
          )}
          <div className="space-y-2">
            {rows.map((r) => (
              <div
                key={r.request_id}
                data-testid={`request-row-${r.request_id}`}
                className="glass rounded-2xl p-3 flex items-center gap-3"
              >
                <Avatar name={r.name} category={r.category} size={44} rounded={12} showSymbol={false} />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold truncate">{r.name}</p>
                  <p className="text-[11px] text-white/50 truncate">@{r.handle} · {r.category}</p>
                  {r.reason && <p className="text-xs text-white/65 line-clamp-2 mt-1">{r.reason}</p>}
                </div>
                <button
                  onClick={() => upvote(r.request_id)}
                  data-testid={`vote-${r.request_id}`}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-bold"
                  style={{ background: "#00F0FF22", color: "#00F0FF", border: "1px solid #00F0FF55" }}
                >
                  <ThumbsUp className="w-3.5 h-3.5" /> {r.vote_count || 0}
                </button>
              </div>
            ))}
          </div>
        </section>

        <p className="text-[10px] text-center text-white/40 mt-4 leading-relaxed">
          Adding an influencer to Fanpuff is at our discretion. Submissions do not guarantee inclusion.
        </p>
        <div className="h-10" />
      </div>
      <BottomNav />
      <RequestInfluencerDialog open={open} onClose={() => setOpen(false)} onSubmitted={load} />
    </div>
  );
}
